import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  Skeleton,
  IconButton,
  Collapse,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import { getPlayerLastSession } from "./funtionsBD/Mainservice";
import { useBrand } from "../context/BrandContext";
import { ExerciseResultDialog } from "./ExerciseResultDialog";
import { isKotExercise, isPersonalizedExercise } from "../utils/teExecutionType";
import { blockTypeLabel } from "../utils/resultsExerciseCatalog";
import "./Userinfo.css";


const PERF_ICONS = ["😞", "😕", "😐", "🙂", "🤩"];
const EXERTION_ICONS = ["💤", "🚶", "🏃", "🔥", "💥"];

const PERF_LABELS = {
  en: ["Poor", "Below avg", "Average", "Good", "Excellent"],
  fr: ["Faible", "Sous moy.", "Moyen", "Bon", "Excellent"],
  de: ["Schlecht", "Unter Avg", "Mittel", "Gut", "Exzellent"],
  es: ["Malo", "Bajo prom.", "Promedio", "Bueno", "Excelente"],
  nl: ["Zwak", "Onder gem.", "Gemiddeld", "Goed", "Uitstekend"],
  pt: ["Fraco", "Abaixo da média", "Médio", "Bom", "Excelente"],
};
const EXERTION_LABELS = {
  en: ["Very light", "Light", "Moderate", "Hard", "Maximum"],
  fr: ["Très léger", "Léger", "Modéré", "Intense", "Maximum"],
  de: ["Sehr leicht", "Leicht", "Mittel", "Schwer", "Maximum"],
  es: ["Muy ligero", "Ligero", "Moderado", "Intenso", "Máximo"],
  nl: ["Zeer licht", "Licht", "Gemiddeld", "Zwaar", "Maximaal"],
  pt: ["Muito leve", "Leve", "Moderado", "Intenso", "Máximo"],
};

const PERF_COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#10b981"];
const EXERTION_COLORS = ["#22c55e", "#84cc16", "#eab308", "#f97316", "#ef4444"];

function normalizeAppLang(lng) {
  const base = (lng || "en").split("-")[0];
  if (base === "gr") return "de";
  if (base === "sp") return "es";
  return base;
}

function getLocalized(obj, prefix, lang) {
  if (!obj) return "";
  return obj[`${prefix}_${lang}`] || obj[`${prefix}_en`] || obj[`${prefix}_fr`] || "";
}

function parseRating(val) {
  if (!val) return null;
  const s = String(val).trim();
  const n = parseInt(s, 10);
  if (n >= 1 && n <= 5) return n;
  const parts = s.split("-");
  if (parts.length === 2) {
    const avg = (parseInt(parts[0], 10) + parseInt(parts[1], 10)) / 2;
    return Math.round(Math.min(5, Math.max(1, avg / 2)));
  }
  return null;
}

function exerciseNeedsAutoeval(ex) {
  const perfVal = parseRating(ex?.autoEval?.exercise_perf);
  const exertionVal = parseRating(ex?.autoEval?.perceived_exertion);
  return !perfVal && !exertionVal;
}

function readStoredGroupGrId() {
  try {
    const g = JSON.parse(localStorage.getItem("grpinf") || "null");
    return g?.groupDetails?.gr_id ?? null;
  } catch {
    return null;
  }
}

function buildSessionPayload(sessionData) {
  if (!sessionData) return sessionData;
  return {
    ...sessionData,
    gr_id: sessionData.gr_id || readStoredGroupGrId(),
  };
}

function RatingPill({ value, icons, colors, labels, label }) {
  const idx = value - 1;
  if (idx < 0 || idx >= icons.length) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, minWidth: 48 }}>
        {label}
      </Typography>
      <Chip
        size="small"
        label={
          <span>
            {icons[idx]} {labels[idx]}
          </span>
        }
        sx={{
          fontWeight: 700,
          fontSize: "0.75rem",
          bgcolor: colors[idx] + "18",
          color: colors[idx],
          border: `1px solid ${colors[idx]}33`,
        }}
      />
    </Box>
  );
}

export function PlayerLastSessionCard({ viewUserId, viewPlayerId, embedded } = {}) {
  const { t, i18n } = useTranslation();
  const { brand } = useBrand();
  const primary = brand?.color_primary || "#0877BC";
  const primaryDark = brand?.color_primary_dark || "#065a8f";
  const lang = normalizeAppLang(i18n.language || localStorage.getItem("i18nextLng"));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedEx, setExpandedEx] = useState(null);
  const [selectedExerciseForModal, setSelectedExerciseForModal] = useState(null);
  const [selectedExerciseInitialTab, setSelectedExerciseInitialTab] = useState("perf");

  const handleExerciseClick = useCallback(
    (ex) => {
      if (!ex?.te_id || !data) return;
      const sessionPayload = buildSessionPayload(data);
      const initialTab =
        !viewUserId &&
        !viewPlayerId &&
        isPersonalizedExercise(ex) &&
        !isKotExercise(ex) &&
        exerciseNeedsAutoeval(ex)
          ? "autoeval"
          : "perf";
      if (embedded) {
        setSelectedExerciseInitialTab(initialTab);
        setSelectedExerciseForModal(ex);
        return;
      }
      window.dispatchEvent(
        new CustomEvent("smarttt-open-exercise-result", {
          detail: { session: sessionPayload, exercise: ex, initialTab },
        })
      );
    },
    [data, embedded, viewUserId, viewPlayerId]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const opts = {};
        if (viewUserId) opts.view_user_id = viewUserId;
        if (viewPlayerId) opts.view_player_id = viewPlayerId;
        const res = await getPlayerLastSession(Object.keys(opts).length ? opts : undefined);
        if (!cancelled) setData(res?.data?.data || null);
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewUserId, viewPlayerId]);

  useEffect(() => {
    const onAutoevalUpdated = (event) => {
      const { te_id, autoEval, ts_id } = event.detail || {};
      if (!te_id || !autoEval || !data?.ts_id || ts_id !== data.ts_id) return;
      setData((prev) => {
        if (!prev?.exercises) return prev;
        return {
          ...prev,
          exercises: prev.exercises.map((ex) =>
            ex.te_id === te_id ? { ...ex, autoEval } : ex
          ),
        };
      });
      setSelectedExerciseForModal((prev) =>
        prev?.te_id === te_id ? { ...prev, autoEval } : prev
      );
    };
    window.addEventListener("smarttt-autoeval-updated", onAutoevalUpdated);
    return () => {
      window.removeEventListener("smarttt-autoeval-updated", onAutoevalUpdated);
    };
  }, [data?.ts_id]);

  const perfLabels = PERF_LABELS[lang] || PERF_LABELS.en;
  const exLabels = EXERTION_LABELS[lang] || EXERTION_LABELS.en;
  const emb = !!embedded;

  if (loading) {
    return (
      <Card
        sx={{
          mx: emb ? 0 : 2,
          my: emb ? 0 : 2,
          borderRadius: emb ? 2 : 3,
        }}
        elevation={0}
      >
        <CardContent sx={{ px: emb ? 1.25 : 2, py: emb ? 1 : 2 }}>
          <Skeleton variant="text" width="60%" height={emb ? 22 : 28} />
          <Skeleton variant="rectangular" height={emb ? 64 : 80} sx={{ borderRadius: 2, mt: 1 }} />
          <Skeleton variant="rectangular" height={emb ? 48 : 60} sx={{ borderRadius: 2, mt: 1 }} />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const sessionDate = data.ts_date
    ? new Date(data.ts_date).toLocaleDateString(lang === "fr" ? "fr-FR" : lang === "de" ? "de-DE" : lang === "es" ? "es-ES" : "en-US", {
        weekday: "short", day: "numeric", month: "short", year: "numeric",
      })
    : "";
  const sessionTime = data.ts_start_time?.slice(0, 5) || "";

  const hasAnyAutoEval = data.exercises?.some((ex) => ex.autoEval);
  const hasAnyFeedback = data.exercises?.some((ex) => ex.coachFeedback?.length > 0);
  const viewingOtherProfile = !!(viewUserId || viewPlayerId);
  const sessionPayload = buildSessionPayload(data);

  return (
    <>
    <Card
      elevation={0}
      sx={{
        mx: emb ? 0 : 2,
        my: emb ? 0 : 2,
        borderRadius: emb ? 2 : 3,
        border: emb
          ? "1px solid rgba(0, 0, 0, 0.08)"
          : `1px solid rgba(var(--brand-primary-rgb, 8, 119, 188), 0.15)`,
        overflow: "hidden",
      }}
    >
      {/* Header gradient */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`,
          color: "#fff",
          px: emb ? 1.5 : 2.5,
          py: emb ? 1.15 : 1.75,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            opacity: 0.85,
            letterSpacing: emb ? 0.6 : 1,
            fontSize: emb ? "0.65rem" : undefined,
            lineHeight: 1.2,
          }}
        >
          {t("lastTrainingSession") || "Last Training Session"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: emb ? 1 : 1.5, mt: 0.25, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <CalendarTodayIcon sx={{ fontSize: emb ? 14 : 16, opacity: 0.9 }} />
            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: emb ? "0.8rem" : undefined }}>
              {sessionDate}
            </Typography>
          </Box>
          {sessionTime && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: emb ? 14 : 16, opacity: 0.9 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: emb ? "0.8rem" : undefined }}>
                {sessionTime}
              </Typography>
            </Box>
          )}
          {data.gr_name && (
            <Chip
              size="small"
              label={data.gr_name}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "#fff",
                fontWeight: 700,
                fontSize: emb ? "0.65rem" : "0.75rem",
                height: emb ? 20 : 24,
              }}
            />
          )}
        </Box>
      </Box>

      <CardContent sx={{ px: emb ? 1.25 : 2, py: emb ? 1 : 1.5 }}>
        {/* Exercise list */}
        {data.exercises?.map((ex, idx) => {
          // Prefer exercise type (and subtype / competency kind). Fall back to
          // catalog name only when type is missing — never "Exercise 1/2/3".
          const exName =
            blockTypeLabel(ex, lang) ||
            [getLocalized(ex.type_ex, "type_name", lang), getLocalized(ex.sub_type_ex, "sub_type_name", lang)]
              .filter(Boolean)
              .join(" - ") ||
            getLocalized(ex.capture_type_ex, "type_name", lang) ||
            ex.ex_name ||
            `${t("exercise")} ${ex.te_order || idx + 1}`;

          const perfVal = parseRating(ex.autoEval?.exercise_perf);
          const exertionVal = parseRating(ex.autoEval?.perceived_exertion);
          const hasFeedback = ex.coachFeedback?.length > 0;
          const hasNotes = ex.playerNotes?.length > 0;
          const isExpanded = expandedEx === ex.te_id;
          const hasDetails = hasFeedback || hasNotes;

          return (
            <Box key={ex.te_id || idx}>
              {idx > 0 && <Divider sx={{ my: emb ? 0.5 : 0.75 }} />}
              <Box
                onClick={() => handleExerciseClick(ex)}
                sx={{
                  py: emb ? 0.65 : 1,
                  px: emb ? 1 : 1.5,
                  mx: emb ? -1 : -1.5,
                  borderRadius: "12px",
                  cursor: "pointer",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.03)",
                  },
                  "&:active": {
                    transform: "scale(0.99)",
                    bgcolor: "rgba(255, 255, 255, 0.05)",
                  },
                }}
              >
                {/* Exercise header */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: emb ? 0.65 : 1, minWidth: 0 }}>
                    <FitnessCenterIcon
                      sx={{ fontSize: emb ? 16 : 18, color: primary, opacity: 0.7, flexShrink: 0 }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        fontSize: emb ? "0.8rem" : undefined,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {exName}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}>
                    {ex.te_duration && (
                      <Chip
                        size="small"
                        label={`${ex.te_duration} ${t("min") || "min"}`}
                        sx={{
                          height: emb ? 20 : 22,
                          fontSize: emb ? "0.65rem" : "0.7rem",
                          fontWeight: 600,
                        }}
                      />
                    )}

                    {hasDetails && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedEx(isExpanded ? null : ex.te_id);
                        }}
                        sx={{
                          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s",
                        }}
                      >
                        <ExpandMoreIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>

                {/* Auto-evaluation inline */}
                {(perfVal || exertionVal) && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: emb ? 1.25 : 2,
                      mt: emb ? 0.5 : 0.75,
                      ml: emb ? 2.75 : 3.5,
                      flexWrap: "wrap",
                    }}
                  >
                    {perfVal && (
                      <RatingPill
                        value={perfVal}
                        icons={PERF_ICONS}
                        colors={PERF_COLORS}
                        labels={perfLabels}
                        label={t("ae_performance_short") || "Perf."}
                      />
                    )}
                    {exertionVal && (
                      <RatingPill
                        value={exertionVal}
                        icons={EXERTION_ICONS}
                        colors={EXERTION_COLORS}
                        labels={exLabels}
                        label={t("ae_exertion_short") || "Effort"}
                      />
                    )}
                  </Box>
                )}

                {/* No autoeval placeholder */}
                {!perfVal && !exertionVal && (
                  <Typography
                    variant="caption"
                    sx={{
                      ml: emb ? 2.75 : 3.5,
                      color: "text.disabled",
                      display: "block",
                      mt: 0.25,
                      fontSize: emb ? "0.65rem" : undefined,
                    }}
                  >
                    {t("noAutoevaluation") || "No self-evaluation"}
                  </Typography>
                )}

                {/* Expanded details: notes + coach feedback */}
                <Collapse in={isExpanded}>
                  <Box sx={{ ml: emb ? 2.75 : 3.5, mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                    {hasNotes && (
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
                          <StickyNote2OutlinedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                            {t("myNotes") || "My Notes"}
                          </Typography>
                        </Box>
                        {ex.playerNotes.map((note, ni) => (
                          <Typography key={ni} variant="body2" sx={{ color: "text.secondary", fontSize: "0.8rem", pl: 0.5 }}>
                            {note}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {hasFeedback && (
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
                          <ChatBubbleOutlineIcon sx={{ fontSize: 15, color: primary }} />
                          <Typography variant="caption" sx={{ fontWeight: 700, color: primary }}>
                            {t("coachFeedback") || "Coach Feedback"}
                          </Typography>
                        </Box>
                        {ex.coachFeedback.map((msg, mi) => (
                          <Box
                            key={msg.message_id || mi}
                            sx={{
                              bgcolor: `${primary}0A`,
                              border: `1px solid ${primary}1A`,
                              borderRadius: 2,
                              px: 1.5,
                              py: 0.75,
                              mb: 0.5,
                            }}
                          >
                            <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                              {msg.text_message}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Box>
            </Box>
          );
        })}

        {/* Summary footer */}
        {data.exercises?.length > 0 && (
          <Box
            sx={{
              mt: emb ? 1 : 1.5,
              pt: emb ? 1 : 1.5,
              borderTop: "1px solid rgba(0,0,0,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: emb ? 0.75 : 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 600, fontSize: emb ? "0.7rem" : undefined }}
            >
              {data.exercises.length} {data.exercises.length === 1 ? t("exercise") || "exercise" : t("exercises") || "exercises"}
              {hasAnyAutoEval && (
                <> &middot; {data.exercises.filter((e) => e.autoEval).length} {t("evaluated") || "evaluated"}</>
              )}
            </Typography>
            {hasAnyFeedback && (
              <Chip
                size="small"
                icon={<ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />}
                label={t("hasCoachFeedback") || "Coach feedback"}
                sx={{
                  height: 22,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  bgcolor: `${primary}12`,
                  color: primary,
                  "& .MuiChip-icon": { color: primary },
                }}
              />
            )}
          </Box>
        )}
      </CardContent>
    </Card>

    {embedded && selectedExerciseForModal && sessionPayload && (
      <ExerciseResultDialog
        open={!!selectedExerciseForModal}
        onClose={() => setSelectedExerciseForModal(null)}
        initialTab={selectedExerciseInitialTab}
        exercise={selectedExerciseForModal}
        session={sessionPayload}
        sessionExercises={data.exercises || []}
        onNavigateExercise={setSelectedExerciseForModal}
        viewUserId={
          viewUserId ||
          (() => {
            try {
              const u = JSON.parse(localStorage.getItem("user") || "{}");
              return u.user_id ? String(u.user_id) : null;
            } catch {
              return null;
            }
          })()
        }
        viewPlayerId={
          viewPlayerId ||
          (data.player_id != null ? String(data.player_id) : null)
        }
        localeCode={lang}
        onAutoevalUpdated={({ te_id, autoEval }) => {
          setData((prev) => {
            if (!prev?.exercises) return prev;
            return {
              ...prev,
              exercises: prev.exercises.map((ex) =>
                ex.te_id === te_id ? { ...ex, autoEval } : ex
              ),
            };
          });
          setSelectedExerciseForModal((prev) =>
            prev?.te_id === te_id ? { ...prev, autoEval } : prev
          );
        }}
      />
    )}
    </>
  );
}
